import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Trash2, Check, Clock, User, Flag, History } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SegmentedTabBar } from '@/components/ui/segmented-tabs';

type TabValue = 'active' | 'history';

interface Reporter {
  id: string;
  name: string;
}

interface PostAuthor {
  id: string;
  name: string;
}

interface PostDetails {
  id: string;
  type: string;
  createdAt: string;
  author: PostAuthor;
  content: string;
  images?: string[];
  videos?: string[];
  pollQuestion?: string | null;
  pollOptions?: Array<{ text: string; order: number }>;
}

interface ReportedPostReport {
  reportId: string;
  reasons: string[];
  descriptions: string[];
  status: string;
  createdAt: string;
  reporters: Reporter[];
  post: PostDetails;
}

export default function ReportedPostsPage() {
  const { communityId } = useParams();
  const [reports, setReports] = useState<ReportedPostReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('active');
  const [actionId, setActionId] = useState<string | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [reportToRemove, setReportToRemove] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/communities/${communityId}/reported-posts?tab=${activeTab}`
      );
      setReports(response.data.reports || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch reported posts');
    } finally {
      setLoading(false);
    }
  }, [communityId, activeTab]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleAction = async (reportId: string, action: 'REMOVE' | 'KEEP') => {
    if (!communityId) return;

    setActionId(reportId);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}/reported-posts`, {
        reportId,
        action,
      });

      toast.success(
        action === 'REMOVE' ? 'Post removed successfully' : 'Reports dismissed successfully'
      );

      // Update local state
      const targetReport = reports.find((r) => r.reportId === reportId);
      if (targetReport) {
        setReports((prev) => prev.filter((r) => r.post.id !== targetReport.post.id));
      } else {
        setReports((prev) => prev.filter((r) => r.reportId !== reportId));
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update reported post status');
    } finally {
      setActionId(null);
    }
  };

  const getReasonBadgeColor = (reason: string) => {
    switch (reason) {
      case 'SPAM':
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50';
      case 'INAPPROPRIATE':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50';
      case 'HARASSMENT':
        return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/50';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-800';
    }
  };

  return (
    <div className="px-1 py-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 md:space-y-8 pb-10 max-w-6xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <Flag className="h-8 w-8 text-primary" />
            Reported Posts
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Review posts flagged by community members. You can choose to remove the post permanently
            or keep it in the feed.
          </p>
        </div>
      </div>

      <div className="flex justify-start">
        <SegmentedTabBar
          items={[
            { value: 'active', label: 'Active Reports', icon: Flag },
            { value: 'history', label: 'History', icon: History },
          ]}
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as TabValue)}
          variant="pill"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
        </div>
      ) : reports.length === 0 ? (
        <Card className="glass-card border-border/70 shadow-lg overflow-hidden">
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              {activeTab === 'active' ? (
                <Check className="h-8 w-8 text-primary" />
              ) : (
                <History className="h-8 w-8 text-primary" />
              )}
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {activeTab === 'active' ? 'All Clear!' : 'No History'}
            </h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm">
              {activeTab === 'active'
                ? 'There are no pending post reports. Your community is clean and safe.'
                : 'There are no resolved or dismissed reports yet.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {reports.map((report) => (
            <Card
              key={report.reportId}
              className="glass-card border-border/70 shadow-md hover:shadow-lg transition-shadow overflow-hidden"
            >
              <div className="border-b border-border/50 bg-muted/20 px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  {report.reasons.map((reason, idx) => (
                    <Badge
                      key={`${reason}-${idx}`}
                      variant="outline"
                      className={cn(
                        'text-xs font-semibold px-2.5 py-0.5 rounded-full',
                        getReasonBadgeColor(reason)
                      )}
                    >
                      {reason}
                    </Badge>
                  ))}
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    Reported {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                  </span>
                  <span className="text-xs text-muted-foreground flex flex-wrap items-center gap-1">
                    by
                    {report.reporters.length <= 2 ? (
                      report.reporters.map((reporter, i) => (
                        <span
                          key={reporter.id}
                          className="font-semibold text-foreground bg-muted px-1.5 py-0.5 rounded-md"
                        >
                          {reporter.name}
                          {i < report.reporters.length - 1 ? ',' : ''}
                        </span>
                      ))
                    ) : (
                      <>
                        {report.reporters.slice(0, 2).map((reporter) => (
                          <span
                            key={reporter.id}
                            className="font-semibold text-foreground bg-muted px-1.5 py-0.5 rounded-md"
                          >
                            {reporter.name},
                          </span>
                        ))}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="font-semibold text-foreground bg-muted hover:bg-muted/80 px-1.5 py-0.5 rounded-md text-xs cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50">
                              +{report.reporters.length - 2} more
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="w-48 max-h-[250px] overflow-y-auto"
                          >
                            {report.reporters.slice(2).map((reporter) => (
                              <DropdownMenuItem
                                key={reporter.id}
                                className="text-xs cursor-default"
                              >
                                {reporter.name}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </span>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3.5 rounded-xl bg-slate-500/5 border border-slate-500/10 text-sm text-foreground/90 space-y-1">
                    <div className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 mb-2">
                      <Flag className="h-4 w-4 shrink-0 text-slate-500" />
                      Reasons:
                    </div>
                    <ul className="pl-6 text-muted-foreground font-medium list-disc space-y-1">
                      {report.reasons.map((reason, i) => (
                        <li key={i}>{reason}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-3.5 rounded-xl bg-amber-500/5 border border-amber-500/10 text-sm text-foreground/90 space-y-1">
                    <div className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5 mb-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                      Descriptions:
                    </div>
                    {report.descriptions.length > 0 ? (
                      <ul className="pl-6 text-muted-foreground list-disc space-y-1">
                        {report.descriptions.map((desc, i) => (
                          <li key={i} className="whitespace-pre-wrap">
                            {desc}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="pl-6 text-muted-foreground">
                        No additional descriptions provided.
                      </p>
                    )}
                  </div>
                </div>

                <div className="p-5 rounded-2xl border border-border bg-card/40 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground truncate">
                          {report.post.author.name}
                        </div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">
                          Post Author
                        </div>
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className="text-[10px] uppercase font-bold tracking-wide shrink-0 whitespace-nowrap"
                    >
                      {report.post.type === 'POLL'
                        ? 'Poll'
                        : `${report.post.type.replace('_', ' ')} Post`}
                    </Badge>
                  </div>

                  {report.post.type === 'POLL' ? (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground leading-snug">
                        {report.post.pollQuestion || report.post.content || 'Poll'}
                      </p>
                      <ul className="space-y-2">
                        {(report.post.pollOptions || []).map((option, index) => (
                          <li
                            key={`${option.order}-${index}`}
                            className="rounded-xl border border-border/70 bg-muted/30 px-3.5 py-2 text-sm text-foreground"
                          >
                            {option.text}
                          </li>
                        ))}
                      </ul>
                      {(!report.post.pollOptions || report.post.pollOptions.length === 0) && (
                        <p className="text-sm text-muted-foreground">No options available</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-foreground/95 whitespace-pre-wrap leading-relaxed">
                      {report.post.content}
                    </div>
                  )}

                  {report.post.images && report.post.images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {report.post.images.map((img, i) => (
                        <div
                          key={i}
                          className="aspect-video rounded-xl overflow-hidden border border-border bg-muted"
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  {report.post.videos && report.post.videos.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                      {report.post.videos.map((vid, i) => (
                        <div
                          key={i}
                          className="aspect-video rounded-xl overflow-hidden border border-border bg-muted"
                        >
                          <video src={vid} controls className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
                  {activeTab === 'active' ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-none h-10 px-5 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-900/50 dark:text-emerald-400 dark:hover:bg-emerald-950/20 shadow-sm"
                        disabled={actionId !== null}
                        onClick={() => handleAction(report.reportId, 'KEEP')}
                      >
                        {actionId === report.reportId ? (
                          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="mr-1.5 h-4 w-4" />
                        )}
                        Don't Remove
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1 sm:flex-none h-10 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow-sm"
                        disabled={actionId !== null}
                        onClick={() => {
                          setReportToRemove(report.reportId);
                          setConfirmRemoveOpen(true);
                        }}
                      >
                        {actionId === report.reportId ? (
                          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="mr-1.5 h-4 w-4" />
                        )}
                        Remove Post
                      </Button>
                    </>
                  ) : (
                    <Badge
                      variant={report.status === 'RESOLVED' ? 'destructive' : 'default'}
                      className={cn(
                        'px-3 py-1.5 text-sm font-semibold rounded-lg border',
                        report.status === 'RESOLVED'
                          ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900/50'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900/50'
                      )}
                    >
                      {report.status === 'RESOLVED' ? 'Post Removed' : 'Report Dismissed'}
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        <AlertDialogContent className="w-[calc(100%-2rem)] sm:w-full rounded-2xl border-border bg-white dark:bg-zinc-950">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-foreground">
              Remove Post?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground leading-relaxed">
              Are you sure you want to remove this post from the community feed? This action is
              permanent and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl font-semibold border-border hover:bg-muted">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive hover:bg-destructive/95 text-destructive-foreground font-semibold shadow-sm"
              onClick={() => {
                if (reportToRemove) {
                  handleAction(reportToRemove, 'REMOVE');
                }
              }}
            >
              Remove Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
